import { auth } from '../firebase';
import { useUserStore, scopedStorageKey } from '../store/userStore';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type FeedbackType = 'feature_rating' | 'usefulness' | 'nps' | 'freeform';
export type ContentType = 'brief' | 'insight' | 'risk_summary' | 'suggested_action' | 'ticket_summary';
export type FreeformCategory = 'bug' | 'idea' | 'praise' | 'other';

export interface FeedbackInput {
  feedbackType: FeedbackType;
  sessionId?: string;
  route?: string;
  clientTs?: string;
  appVersion?: string;
  featureRating?: { featureKey: string; rating: 1 | 2 | 3 | 4 | 5; comment?: string };
  usefulness?: { contentType: ContentType; contentId: string; useful: boolean; comment?: string };
  nps?: { score: number; reason?: string };
  freeform?: { category: FreeformCategory; message: string; attachedRoute?: string };
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const FEATURE_KEYS = {
  ACTION_MODAL: 'action_modal',
  RISK_PANEL: 'risk_panel',
  MORNING_BRIEF: 'morning_brief',
  INSIGHTS: 'insights',
} as const;

export const NPS_STORAGE_KEY = '__octopm_nps_last_prompted';

// ---------------------------------------------------------------------------
// Auth helper
// ---------------------------------------------------------------------------

const BASE_URL =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ||
  'http://localhost:8000/api/v1';

async function getAuthHeaders(): Promise<Record<string, string>> {
  const token = await auth.currentUser?.getIdToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

// ---------------------------------------------------------------------------
// Submit
// ---------------------------------------------------------------------------

export async function submitFeedback(
  input: FeedbackInput,
): Promise<{ feedbackId: string }> {
  const payload: FeedbackInput = {
    ...input,
    route: input.route ?? window.location.pathname,
    clientTs: input.clientTs ?? new Date().toISOString(),
  };

  const headers = await getAuthHeaders();
  const res = await fetch(`${BASE_URL}/feedback`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    let message = `API error ${res.status}`;
    try {
      const body = (await res.json()) as { detail?: unknown; message?: string };
      const detail = body.detail;
      message =
        (typeof detail === 'string'
          ? detail
          : (detail as Record<string, string>)?.message) ??
        body.message ??
        message;
    } catch {
      // ignore parse error
    }
    throw new Error(message);
  }

  const envelope = (await res.json()) as { data: { feedbackId: string } };
  return envelope.data;
}

// ---------------------------------------------------------------------------
// NPS — server-side eligibility + state
// ---------------------------------------------------------------------------

export async function checkNpsEligibility(): Promise<boolean> {
  try {
    const headers = await getAuthHeaders();
    const res = await fetch(`${BASE_URL}/feedback/nps-eligibility`, { headers });
    if (!res.ok) return false;
    const json = (await res.json()) as { data: { eligible: boolean } };
    return json.data.eligible ?? false;
  } catch {
    return false;
  }
}

export async function recordNpsState(
  action: 'snoozed' | 'dismissed',
): Promise<void> {
  try {
    const headers = await getAuthHeaders();
    await fetch(`${BASE_URL}/feedback/nps-state`, {
      method: 'PATCH',
      headers,
      body: JSON.stringify({ action }),
    });
  } catch {
    // fire-and-forget — not critical
  }
}

// ---------------------------------------------------------------------------
// Cadence guards (feature-rating cooldown — stays client-side)
// ---------------------------------------------------------------------------

const COOLDOWN_PREFIX = '__octopm_fb_fr_';
const COOLDOWN_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

// featureKey is a global constant (see FEATURE_KEYS above), not per-tenant
// or per-user data — scoping by entityId+userId (see
// userStore.scopedStorageKey) prevents the 14-day cooldown from being
// shared across every account in a browser profile. Returns null when the
// entity/user scope isn't known yet, in which case the cooldown simply
// isn't read/written for this call.
function cooldownKey(featureKey: string): string | null {
  const sessionConfig = useUserStore.getState().sessionConfig;
  return scopedStorageKey(`${COOLDOWN_PREFIX}${featureKey}`, sessionConfig?.entityId, sessionConfig?.userId);
}

export function hasFeedbackCooldown(featureKey: string): boolean {
  try {
    const key = cooldownKey(featureKey);
    if (!key) return false;
    const raw = localStorage.getItem(key);
    if (!raw) return false;
    return Date.now() - Number(raw) < COOLDOWN_MS;
  } catch {
    return false;
  }
}

export function setFeedbackCooldown(featureKey: string): void {
  try {
    const key = cooldownKey(featureKey);
    if (!key) return;
    localStorage.setItem(key, String(Date.now()));
  } catch {
    // ignore
  }
}
